// BatchImportForm.js
import React, { useState, useRef } from 'react';
import { useAuth } from "react-oidc-context";
import { licenseService } from './apiService';
import Papa from 'papaparse';
import { Button } from './components/Button';
import { Badge } from './components/Badge';
import './forms.css';
import './BatchImportForm.css';

function BatchImportForm({ onBatchComplete }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [forceRebind, setForceRebind] = useState(false);
  const [defaultDuration, setDefaultDuration] = useState(12); // Default duration for licenses without specified duration
  const fileInputRef = useRef(null);
  
  const auth = useAuth();
  
  const handleClear = () => {
    setFile(null);
    setPreview([]);
    setResults(null);
    setError(null);
    setProgress({ current: 0, total: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setFile(null);
      setPreview([]);
      return;
    }
    
    setFile(selectedFile);
    
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      preview: 5, // Preview first 5 rows
      complete: (results) => {
        setPreview(results.data);
        
        const headers = results.meta.fields || [];
        if (!headers.includes('serialNumber') || !headers.includes('deviceId')) {
          setError("CSV must contain 'serialNumber' and 'deviceId' columns");
        } else {
          setError(null);
        }
      },
      error: (err) => {
        setError(`Error parsing CSV: ${err.message}`);
        setPreview([]);
      }
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file || error) return;
    
    const token = auth.user?.access_token;
    if (!token) {
      setError("No access token found. Please log in.");
      return;
    }
    
    setIsProcessing(true);
    setResults(null);
    setError(null);
    
    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const rows = results.data;
          
          if (!rows.length) {
            throw new Error("No data rows found in CSV");
          }
          
          // Process in batches of 100 to avoid exceeding API limits
          const BATCH_SIZE = 100;
          let processedCount = 0;
          let batchResults = {
            totalProcessed: rows.length,
            successful: 0,
            failed: 0,
            details: []
          };
          
          setProgress({ current: 0, total: rows.length });
          
          // Process in chunks to avoid sending too much data at once
          for (let i = 0; i < rows.length; i += BATCH_SIZE) {
            const chunk = rows.slice(i, i + BATCH_SIZE);
            
            // Format the data for the API, including optional licenseTier if present
            const items = chunk.map(row => {
              const item = {
                serialNumber: row.serialNumber?.trim(),
                deviceId: row.deviceId?.trim()
              };
              
              // Add licenseTier if present in the CSV
              if (row.licenseTier) {
                item.licenseTier = row.licenseTier.trim();
              }
              
              // Add customer fields if present
              if (row.customerName) {
                item.customerName = row.customerName.trim();
              }
              if (row.customerNumber) {
                item.customerNumber = row.customerNumber.trim();
              }
              
              // Add country if present
              if (row.country) {
                item.country = row.country.trim().toUpperCase(); // Normalize to uppercase
              }
              
            // Add duration if present in the CSV, or use default
            if (row.durationMonths) {
                // Ensure it's a valid number between 1-60 months
                const duration = parseInt(row.durationMonths.trim(), 10);
                if (!isNaN(duration) && duration > 0 && duration <= 60) {
                  item.durationMonths = duration;
                } else {
                  item.durationMonths = defaultDuration; // Use default if invalid
                }
              } else {
                item.durationMonths = defaultDuration; // Use default if not specified
              }
              
              return item;
            }).filter(item => item.serialNumber && item.deviceId);
            
            try {
              // Call the batch create API
              const response = await licenseService.batchCreateLicenses(items, forceRebind, token);
              
              // Merge the results
              batchResults.successful += response.successful;
              batchResults.failed += response.failed;
              batchResults.details = [...batchResults.details, ...response.details];
              
              // Update progress
              processedCount += chunk.length;
              setProgress({ current: processedCount, total: rows.length });
            } catch (batchError) {
              // If batch call fails, mark all items in this batch as failed
              const failedItems = items.map(item => ({
                serialNumber: item.serialNumber,
                deviceId: item.deviceId,
                status: 'error',
                message: batchError.message || 'Batch processing failed'
              }));
              
              batchResults.failed += items.length;
              batchResults.details = [...batchResults.details, ...failedItems];
              
              // Update progress
              processedCount += chunk.length;
              setProgress({ current: processedCount, total: rows.length });
            }
          }
          
          setResults(batchResults);
          
          if (onBatchComplete) {
            onBatchComplete(batchResults);
          }
        },
        error: (err) => {
          throw new Error(`Error processing CSV: ${err.message}`);
        }
      });
    } catch (err) {
      setError(`Batch processing failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate progress percentage
  const progressPercentage = progress.total > 0 
    ? Math.round((progress.current / progress.total) * 100) 
    : 0;
  
  return (
    <section className="form-card">
      <h2 className="form-card__title">Batch License Creation</h2>
      <form onSubmit={handleSubmit} className="batch-form">
        <div className="form-field">
          <label htmlFor="csvFile" className="form-label">
            Upload a CSV file to create licenses with device bindings.
          </label>
          <input
            type="file"
            id="csvFile"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileChange}
            disabled={isProcessing}
            aria-describedby="fileHelpText"
            className="form-input"
          />
          <div id="fileHelpText" className="form-hint">
            Required columns: 'serialNumber', 'deviceId'.
            Optional columns: 'licenseTier' (defaults to 'BASIC'), 'customerName', 'customerNumber', 'country', 'durationMonths' (defaults to selected value below). Customer number identifies the institution and cannot change; omit it to preserve an existing assignment. Device replacement requires the unregister/register process.
          </div>
        </div>

        <div className="batch-form__inline-options">
          <div className="batch-form__checkbox">
            <input
              type="checkbox"
              id="forceRebind"
              checked={forceRebind}
              onChange={(e) => setForceRebind(e.target.checked)}
              disabled={isProcessing}
              aria-label="Update existing licences on the same device"
            />
            <label htmlFor="forceRebind">
              Update existing licences on the same device (preserves institution ownership)
            </label>
          </div>

          <div className="form-field form-field--inline">
            <label htmlFor="defaultDuration" className="form-label">
              Default Duration
            </label>
            <select
              id="defaultDuration"
              className="form-select"
              value={defaultDuration}
              onChange={(e) => setDefaultDuration(parseInt(e.target.value, 10))}
              disabled={isProcessing}
              aria-label="Default license duration"
            >
              <option value="1">1 month</option>
              <option value="3">3 months</option>
              <option value="6">6 months</option>
              <option value="12">12 months</option>
              <option value="24">24 months</option>
              <option value="36">36 months</option>
              <option value="60">60 months</option>
            </select>
            <span className="form-hint">
              Applied if CSV doesn't specify 'durationMonths'
            </span>
          </div>
        </div>

        {preview.length > 0 && (
          <div className="batch-preview">
            <h3 className="batch-preview__title">Preview</h3>
            <div className="batch-preview__scroll">
              <table className="batch-table">
                <thead>
                  <tr>
                    {Object.keys(preview[0]).map(key => (
                      <th key={key}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, index) => (
                    <tr key={index}>
                      {Object.values(row).map((value, i) => (
                        <td key={i}>{value || '-'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="batch-preview__caption">Showing first {preview.length} rows</p>
            </div>
          </div>
        )}

        {error && (
          <div role="alert" className="form-message form-message--error">
            {error}
          </div>
        )}

        <div className="form-actions">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClear}
            disabled={isProcessing}
          >
            Clear
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!file || !!error || isProcessing}
            loading={isProcessing}
          >
            Process Batch
          </Button>
        </div>
      </form>

      {/* Progress bar (visible during processing) */}
      {isProcessing && (
        <div className="batch-progress">
          <div role="status" aria-live="polite" className="batch-progress__label">
            Processing: {progress.current} of {progress.total} ({progressPercentage}%)
          </div>
          <div className="batch-progress__track">
            <div
              className="batch-progress__bar"
              style={{ width: `${progressPercentage}%` }}
              aria-hidden="true"
            />
          </div>
        </div>
      )}

      {/* Results section */}
      {results && (
        <div className="batch-results">
          <h3 className="batch-results__title">Batch Processing Results</h3>
          <p className="batch-results__summary">
            <strong>Total processed:</strong> {results.total}
            <span className="batch-results__divider">|</span>
            <strong>Successful:</strong> <Badge variant="success">{results.successful}</Badge>
            <span className="batch-results__divider">|</span>
            <strong>Failed:</strong> <Badge variant="warning">{results.failed}</Badge>
          </p>

          <div className="batch-preview__scroll">
            <table className="batch-table">
              <thead>
                <tr>
                  <th>Serial Number</th>
                  <th>Device ID</th>
                  <th>Status</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {results.details.map((detail, index) => (
                  <tr key={index} className={detail.status === 'error' ? 'batch-row--error' : 'batch-row--success'}>
                    <td>{detail.serialNumber}</td>
                    <td>{detail.deviceId}</td>
                    <td>
                      {detail.status === 'error' ? (
                        <Badge variant="warning">Error</Badge>
                      ) : (
                        <Badge variant="success">Success</Badge>
                      )}
                    </td>
                    <td>{detail.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export default BatchImportForm;